import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus, Pencil, Trash2, Mail, Phone, MessageSquare, User, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';

export default function ContactsTab({ clientId, contacts, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    fonction: '',
    email: '',
    telephone: '',
    whatsapp: '',
    niveau_influence: 'Prescripteur',
    notes: ''
  });

  const handleAdd = () => {
    setEditingContact(null);
    setFormData({
      nom: '',
      prenom: '',
      fonction: '',
      email: '',
      telephone: '',
      whatsapp: '',
      niveau_influence: 'Prescripteur',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData(contact);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.prenom || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      if (editingContact) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/contacts/${editingContact.id}`,
          formData
        );
        toast.success('Contact modifié avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/contacts`,
          formData
        );
        toast.success('Contact ajouté avec succès');
      }
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/contacts/${contactId}`);
      toast.success('Contact supprimé');
      onUpdate();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getNiveauBadge = (niveau) => {
    const colors = {
      'Décideur': 'bg-red-100 text-red-700',
      'Prescripteur': 'bg-blue-100 text-blue-700',
      'Influenceur': 'bg-green-100 text-green-700'
    };
    return colors[niveau] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Contacts Clés</h3>
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          Ajouter un Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            Aucun contact enregistré. Cliquez sur "Ajouter un Contact" pour commencer.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <Card key={contact.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">
                        {contact.prenom} {contact.nom}
                      </h4>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {contact.fonction}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(contact)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDelete(contact.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                      {contact.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{contact.telephone}</span>
                  </div>
                  {contact.whatsapp && (
                    <div className="flex items-center gap-2 text-sm">
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      <span>{contact.whatsapp}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <Badge className={getNiveauBadge(contact.niveau_influence)}>
                    {contact.niveau_influence}
                  </Badge>
                </div>

                {contact.notes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                    <p className="font-medium mb-1">Notes:</p>
                    <p>{contact.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Ajout/Édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Modifier le contact' : 'Ajouter un contact'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Jean"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Dupont"
                />
              </div>
            </div>

            <div>
              <Label>Fonction *</Label>
              <Input
                value={formData.fonction}
                onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                placeholder="Responsable Communication"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="j.dupont@entreprise.com"
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="+224 XXX XXX XXX"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>WhatsApp (optionnel)</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+224 XXX XXX XXX"
                />
              </div>
              <div>
                <Label>Niveau d'influence *</Label>
                <Select
                  value={formData.niveau_influence}
                  onValueChange={(val) => setFormData({ ...formData, niveau_influence: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Décideur">Décideur</SelectItem>
                    <SelectItem value="Prescripteur">Prescripteur</SelectItem>
                    <SelectItem value="Influenceur">Influenceur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Informations complémentaires..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmit}>
                {editingContact ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
